import React, {useMemo, useState} from 'react';
import {
    Alert,
    Box,
    Button,
    Chip,
    Collapse,
    Divider,
    MenuItem,
    Select,
    Stack,
    Tooltip,
    Typography,
} from '@mui/material';
import HistoryIcon from '@mui/icons-material/History';
import {CARVER_FACTORS} from '../../../hooks/useCarver';
import TabEmpty from './TabEmpty';
import TabLoading from './TabLoading';

const UNSCORED = '';

const FACTOR_META = {
    CRITICALITY: {label: 'Criticality', hint: 'How important the target is to the system it belongs to.'},
    ACCESSIBILITY: {label: 'Accessibility', hint: 'How reachable the target is.'},
    RECUPERABILITY: {label: 'Recuperability', hint: 'How quickly the target recovers — fast recovery scores low.'},
    VULNERABILITY: {label: 'Vulnerability', hint: 'How susceptible the target is to the means available.'},
    EFFECT: {label: 'Effect', hint: 'The consequence of acting on the target.'},
    RECOGNIZABILITY: {label: 'Recognizability', hint: 'How readily the target can be identified.'},
};

const CHANGE_REASON_LABEL = {
    EDIT: null,
    MERGE_CARRIED_OVER: 'carried over by merge',
    MERGE_DISCARDED: 'discarded by merge',
};

const formatValue = (value) => (value == null ? 'unscored' : value);

const FactorRow = ({factor, value, minScore, maxScore, disabled, onChange}) => {
    const meta = FACTOR_META[factor] ?? {label: factor, hint: ''};
    const options = useMemo(
        () => Array.from({length: maxScore - minScore + 1}, (_, i) => minScore + i),
        [minScore, maxScore],
    );

    return (
        <Stack direction="row" spacing={2} alignItems="center" sx={{py: 1.5}}>
            <Box flex={1} minWidth={0}>
                <Tooltip title={meta.hint} placement="top-start">
                    <Typography variant="subtitle2" fontWeight={600}>{meta.label}</Typography>
                </Tooltip>
                <Typography variant="caption" color="text.secondary">{meta.hint}</Typography>
            </Box>
            <Select
                size="small"
                value={value == null ? UNSCORED : String(value)}
                disabled={disabled}
                displayEmpty
                onChange={(e) => onChange(e.target.value === UNSCORED ? null : Number(e.target.value))}
                sx={{minWidth: 128}}
                renderValue={(selected) =>
                    selected === UNSCORED
                        ? <Typography variant="body2" color="text.disabled">Unscored</Typography>
                        : selected}
            >
                <MenuItem value={UNSCORED}>
                    <Typography variant="body2" color="text.disabled">Unscored</Typography>
                </MenuItem>
                {options.map((option) => (
                    <MenuItem key={option} value={String(option)}>{option}</MenuItem>
                ))}
            </Select>
        </Stack>
    );
};

/**
 * CARVER matrix editor. Scores are global — any signed-in analyst can change them — so every
 * change is attributed and the history is one click away.
 */
const CarverTab = ({
                       factors,
                       total,
                       scoredFactorCount,
                       minScore = 1,
                       maxScore = 10,
                       loading,
                       error,
                       saving,
                       canEdit = true,
                       onSetFactor,
                       history = [],
                       historyLoading,
                       pinEnabled = false,
                       carverPinned = false,
                       onToggleCarverPin,
                   }) => {
    const [historyOpen, setHistoryOpen] = useState(false);

    if (loading) return <TabLoading/>;
    if (error && !factors) return <TabEmpty message="Failed to load the CARVER matrix."/>;

    const complete = total != null;
    const maxTotal = maxScore * CARVER_FACTORS.length;

    return (
        <Stack sx={{p: 2}} spacing={1}>
            {error && <Alert severity="error">{error.message || 'Failed to save the CARVER score.'}</Alert>}

            <Stack direction="row" alignItems="center" spacing={1} sx={{pb: 1}}>
                {complete ? (
                    <Chip
                        color="primary"
                        label={`Total ${total} / ${maxTotal}`}
                        sx={{fontWeight: 700}}
                    />
                ) : (
                    <Chip
                        variant="outlined"
                        label={`Incomplete — ${scoredFactorCount} of ${CARVER_FACTORS.length} factors scored`}
                    />
                )}
                <Box flex={1}/>
                {pinEnabled ? (
                    <Button
                        size="small"
                        variant="text"
                        onClick={() => onToggleCarverPin?.(!carverPinned)}
                    >
                        {carverPinned ? 'Unpin from card' : 'Pin to card'}
                    </Button>
                ) : null}
                <Button
                    size="small"
                    startIcon={<HistoryIcon/>}
                    onClick={() => setHistoryOpen((open) => !open)}
                >
                    {historyOpen ? 'Hide history' : 'History'}
                </Button>
            </Stack>

            {!canEdit && (
                <Alert severity="info">
                    Sign in to change these scores — every change is recorded against a user.
                </Alert>
            )}

            <Divider/>

            <Box>
                {CARVER_FACTORS.map((factor, index) => (
                    <React.Fragment key={factor}>
                        {index > 0 && <Divider/>}
                        <FactorRow
                            factor={factor}
                            value={factors?.[factor] ?? null}
                            minScore={minScore}
                            maxScore={maxScore}
                            disabled={saving || !canEdit}
                            onChange={(next) => onSetFactor(factor, next)}
                        />
                    </React.Fragment>
                ))}
            </Box>

            <Typography variant="caption" color="text.secondary" sx={{pt: 1}}>
                A total is only produced once all six factors are scored. CARVER ranks candidates
                against each other, so a total is meaningful next to other assessed entities rather
                than on its own.
            </Typography>

            <Collapse in={historyOpen} unmountOnExit>
                <Divider sx={{my: 1}}/>
                {historyLoading ? (
                    <TabLoading/>
                ) : history.length === 0 ? (
                    <TabEmpty message="No changes recorded yet."/>
                ) : (
                    <Stack spacing={1} sx={{py: 1}}>
                        {history.map((entry) => {
                            const reason = CHANGE_REASON_LABEL[entry.changeReason];
                            return (
                                <Stack key={entry.id} spacing={0.25}>
                                    <Typography variant="body2">
                                        <strong>{FACTOR_META[entry.factor]?.label ?? entry.factor}</strong>
                                        {` ${formatValue(entry.oldValue)} → ${formatValue(entry.newValue)}`}
                                        {reason ? ` (${reason})` : ''}
                                    </Typography>
                                    <Typography variant="caption" color="text.secondary">
                                        {entry.changedBy}
                                        {entry.changedAt ? ` · ${new Date(entry.changedAt).toLocaleString()}` : ''}
                                    </Typography>
                                    {entry.rationale && (
                                        <Typography variant="caption" color="text.secondary" fontStyle="italic">
                                            {entry.rationale}
                                        </Typography>
                                    )}
                                </Stack>
                            );
                        })}
                    </Stack>
                )}
            </Collapse>
        </Stack>
    );
};

export default CarverTab;
